export const rowIndexToLabel = (zeroBasedIndex) => {
  let n = Number(zeroBasedIndex) + 1;
  if (!Number.isInteger(n) || n <= 0) return "";

  let label = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    label = String.fromCharCode(65 + rem) + label;
    n = Math.floor((n - 1) / 26);
  }
  return label;
};

export const normalizeRowLabel = (value) => String(value ?? "").trim().toUpperCase();

export const isValidRowLabel = (value) => /^[A-Z]+$/.test(normalizeRowLabel(value));

export const rowLabelToIndex = (label) => {
  const normalized = normalizeRowLabel(label);
  if (!isValidRowLabel(normalized)) return null;

  let index = 0;
  for (const character of normalized) {
    index = index * 26 + (character.charCodeAt(0) - 64);
  }
  return index - 1;
};

export const parseColumnStart = (value) => {
  const text = String(value ?? "").trim();
  if (!/^\d+$/.test(text)) return null;
  const parsed = Number(text);
  return parsed >= 1 ? parsed : null;
};

const parsePositiveCount = (value) => {
  const text = String(value ?? "").trim();
  if (!/^\d+$/.test(text)) return null;
  const parsed = Number(text);
  return parsed >= 1 ? parsed : null;
};

export const DEFAULT_START_ROW_LABEL = "A";
export const DEFAULT_START_COLUMN_NUMBER = 1;
export const DEFAULT_NUMBER_DIRECTION = "ltr";

export const normalizeNumberDirection = (value) =>
  String(value ?? "").trim().toLowerCase() === "rtl" ? "rtl" : DEFAULT_NUMBER_DIRECTION;

export const sectionOrigin = (section) => ({
  startRowIndex: rowLabelToIndex(section?.start_row_label) ?? 0,
  startColumnNumber: parseColumnStart(section?.start_column_number) ?? DEFAULT_START_COLUMN_NUMBER,
  numberDirection: normalizeNumberDirection(section?.number_direction),
});

export const validateGridSettings = ({
  rowCount,
  columnCount,
  startRowLabel,
  startColumnNumber,
} = {}) => {
  if (!parsePositiveCount(rowCount)) return "Rows must be a positive whole number.";
  if (!parsePositiveCount(columnCount)) return "Columns must be a positive whole number.";
  if (!isValidRowLabel(startRowLabel)) {
    return "Starting row must be letters only, such as A, D, or AA.";
  }
  if (!parseColumnStart(startColumnNumber)) {
    return "Starting column must be a positive whole number.";
  }
  return "";
};

export const rowLabelsForCount = (rowCount, startRowIndex = 0) =>
  Array.from({ length: Math.max(0, Number(rowCount) || 0) }, (_, index) =>
    rowIndexToLabel((Number(startRowIndex) || 0) + index)
  );

export const columnNumbersForCount = (
  columnCount,
  startColumnNumber = DEFAULT_START_COLUMN_NUMBER
) => {
  const start = parseColumnStart(startColumnNumber) ?? DEFAULT_START_COLUMN_NUMBER;
  return Array.from(
    { length: Math.max(0, Number(columnCount) || 0) },
    (_, index) => start + index
  );
};

export const pageColumnNumbers = (page) =>
  columnNumbersForCount(page?.columns || 0, page?.start_column_number);

export const makeSeatLabel = (rowLabel, columnNumber) => `${rowLabel}${columnNumber}`;

const originStart = (origin = {}) => ({
  startRowIndex: Number(origin.startRowIndex) || 0,
  startColumnNumber:
    parseColumnStart(origin.startColumnNumber) ?? DEFAULT_START_COLUMN_NUMBER,
  numberDirection: normalizeNumberDirection(origin.numberDirection),
});

/**
 * Assigns seat numbers from actual seats only. Empty grid cells are absent
 * from the list, so they do not consume a number. Physical positions stay put.
 */
export const renumberSeats = (seats = [], origin = {}) => {
  const { startRowIndex, startColumnNumber, numberDirection } = originStart(origin);
  const groups = new Map();

  (seats || []).forEach((seat, index) => {
    const rowPosition = Number(seat?.row_position) || 0;
    const columnPosition = Number(seat?.column_position) || 0;
    if (rowPosition < 1 || columnPosition < 1) return;
    if (!groups.has(rowPosition)) groups.set(rowPosition, []);
    groups.get(rowPosition).push({ seat, index });
  });

  const updates = new Map();
  groups.forEach((entries, rowPosition) => {
    const sorted = [...entries].sort((left, right) => {
      const delta =
        (Number(left.seat?.column_position) || 0) - (Number(right.seat?.column_position) || 0);
      return numberDirection === "rtl" ? -delta : delta;
    });
    const rowLabel = rowIndexToLabel(startRowIndex + rowPosition - 1);
    sorted.forEach((entry, seatIndex) => {
      const columnNumber = startColumnNumber + seatIndex;
      updates.set(entry.index, {
        ...entry.seat,
        row_position: rowPosition,
        column_position: Number(entry.seat.column_position),
        row_label: rowLabel,
        column_number: columnNumber,
        seat_label: makeSeatLabel(rowLabel, columnNumber),
      });
    });
  });

  return (seats || []).map((seat, index) => updates.get(index) || seat);
};

export const seatNumbersForLayout = (
  layout = [],
  startColumnNumber = DEFAULT_START_COLUMN_NUMBER,
  numberDirection = DEFAULT_NUMBER_DIRECTION
) => {
  const start = parseColumnStart(startColumnNumber) ?? DEFAULT_START_COLUMN_NUMBER;
  const rtl = normalizeNumberDirection(numberDirection) === "rtl";
  return (layout || []).map((row) => {
    const cells = row || [];
    const numbers = new Array(cells.length).fill(null);
    const occupied = [];
    cells.forEach((cell, index) => {
      if (cell) occupied.push(index);
    });
    const ordered = rtl ? [...occupied].reverse() : occupied;
    ordered.forEach((colIndex, seatIndex) => {
      numbers[colIndex] = start + seatIndex;
    });
    return numbers;
  });
};

const pageColumnCount = (page) => {
  const widths = [
    Number(page?.columns) || 0,
    ...(page?.layout || []).map((row) => (row || []).length),
    ...(page?.seat_numbers || []).map((row) => (row || []).length),
  ];
  return Math.max(0, ...widths);
};

export const resolveSeatNumber = (page, rowIndex, colIndex) => {
  const storedRow = page?.seat_numbers?.[rowIndex];
  if (Array.isArray(storedRow)) {
    const stored = storedRow[colIndex];
    return Number.isInteger(stored) && stored >= 1 ? stored : null;
  }
  const columns = pageColumnNumbers(page);
  return columns[colIndex] ?? colIndex + 1;
};

export const columnHeadersForPage = (page) => {
  if (!Array.isArray(page?.seat_numbers)) return pageColumnNumbers(page);

  const columnCount = pageColumnCount(page);
  const headers = [];
  for (let colIndex = 0; colIndex < columnCount; colIndex += 1) {
    const numbers = new Set();
    (page.seat_numbers || []).forEach((row) => {
      const value = row?.[colIndex];
      if (Number.isInteger(value) && value >= 1) numbers.add(value);
    });
    headers.push(numbers.size === 1 ? [...numbers][0] : colIndex + 1);
  }
  return headers;
};

export const createSeat = (rowPosition, columnPosition, origin = {}) => {
  const startRowIndex = Number(origin.startRowIndex) || 0;
  const startColumnNumber =
    parseColumnStart(origin.startColumnNumber) ?? DEFAULT_START_COLUMN_NUMBER;

  const rowLabel = rowIndexToLabel(startRowIndex + rowPosition - 1);
  const columnNumber = startColumnNumber + columnPosition - 1;

  return {
    row_position: rowPosition,
    column_position: columnPosition,
    row_label: rowLabel,
    column_number: columnNumber,
    seat_label: makeSeatLabel(rowLabel, columnNumber),
  };
};

export const generateSeats = (rowCount, columnCount, origin = {}) => {
  const rows = Math.max(0, Number(rowCount) || 0);
  const cols = Math.max(0, Number(columnCount) || 0);
  const seats = [];

  for (let rowPosition = 1; rowPosition <= rows; rowPosition += 1) {
    for (let columnPosition = 1; columnPosition <= cols; columnPosition += 1) {
      seats.push(createSeat(rowPosition, columnPosition, origin));
    }
  }

  return renumberSeats(seats, origin);
};

export const seatsToLayout = (rowCount, columnCount, seats = []) => {
  const occupied = new Set(
    (seats || []).map((seat) => `${seat.row_position}:${seat.column_position}`)
  );

  return Array.from({ length: Math.max(0, Number(rowCount) || 0) }, (_, rowIndex) =>
    Array.from({ length: Math.max(0, Number(columnCount) || 0) }, (_, colIndex) =>
      occupied.has(`${rowIndex + 1}:${colIndex + 1}`) ? 1 : 0
    )
  );
};

export const layoutToSeats = (layout = [], origin = {}) => {
  const seats = [];

  (layout || []).forEach((row, rowIndex) => {
    (row || []).forEach((cell, colIndex) => {
      if (cell) seats.push(createSeat(rowIndex + 1, colIndex + 1, origin));
    });
  });

  return renumberSeats(seats, origin);
};

export const hasSeatAt = (seats, rowPosition, columnPosition) =>
  (seats || []).some(
    (seat) => seat.row_position === rowPosition && seat.column_position === columnPosition
  );

export const toggleSeatAt = (seats, rowPosition, columnPosition, origin = {}) => {
  const current = seats || [];
  const next = hasSeatAt(current, rowPosition, columnPosition)
    ? current.filter(
        (seat) => !(seat.row_position === rowPosition && seat.column_position === columnPosition)
      )
    : [...current, createSeat(rowPosition, columnPosition, origin)];
  return renumberSeats(next, origin);
};

export const selectRowSeats = (seats, rowPosition, columnCount, origin = {}) => {
  const others = (seats || []).filter((seat) => seat.row_position !== rowPosition);
  const rowSeats = Array.from({ length: Math.max(0, Number(columnCount) || 0) }, (_, index) =>
    createSeat(rowPosition, index + 1, origin)
  );
  return renumberSeats([...others, ...rowSeats], origin);
};

export const clearRowSeats = (seats, rowPosition) =>
  (seats || []).filter((seat) => seat.row_position !== rowPosition);

export const resizeSeats = (seats, rowCount, columnCount, origin = {}) =>
  renumberSeats(
    (seats || []).filter(
      (seat) => seat.row_position <= rowCount && seat.column_position <= columnCount
    ),
    origin
  );

export const wouldResizeDeleteSeats = (seats, rowCount, columnCount) =>
  (seats || []).some(
    (seat) => seat.row_position > rowCount || seat.column_position > columnCount
  );

export const findDuplicateSeatLabels = (seats) => {
  const seen = new Set();
  const duplicates = new Set();

  (seats || []).forEach((seat) => {
    const label = String(seat?.seat_label || "").trim().toUpperCase();
    if (!label) return;
    if (seen.has(label)) duplicates.add(label);
    else seen.add(label);
  });

  return [...duplicates];
};

const seatPositionKey = (seat) =>
  `${Number(seat?.row_position) || 0}:${Number(seat?.column_position) || 0}`;

/**
 * Returns labels that nextSeats would introduce as duplicates relative to
 * currentSeats, or duplicates within nextSeats itself.
 */
export const wouldAddDuplicateSeatLabel = (currentSeats = [], nextSeats = []) => {
  const conflicts = new Set();
  const labelsByPosition = new Map();

  (nextSeats || []).forEach((seat) => {
    const label = String(seat?.seat_label || "").trim().toUpperCase();
    if (!label) return;
    const key = seatPositionKey(seat);
    if (labelsByPosition.has(label) && labelsByPosition.get(label) !== key) {
      conflicts.add(label);
    } else {
      labelsByPosition.set(label, key);
    }
  });

  const nextPositions = new Set((nextSeats || []).map((seat) => seatPositionKey(seat)));
  const currentByPosition = new Map(
    (currentSeats || [])
      .filter((seat) => seat?.row_position && seat?.column_position)
      .map((seat) => [seatPositionKey(seat), seat])
  );

  (nextSeats || []).forEach((seat) => {
    const label = String(seat?.seat_label || "").trim().toUpperCase();
    if (!label) return;
    const key = seatPositionKey(seat);
    const previous = currentByPosition.get(key);
    const previousLabel = String(previous?.seat_label || "").trim().toUpperCase();
    if (!previous || previousLabel !== label) {
      const otherHasLabel = (currentSeats || []).some((other) => {
        const otherKey = seatPositionKey(other);
        if (otherKey === key || !nextPositions.has(otherKey)) return false;
        return String(other?.seat_label || "").trim().toUpperCase() === label;
      });
      if (otherHasLabel) conflicts.add(label);
    }
  });

  return [...conflicts];
};

export const formatDuplicateSeatMessage = (labels = []) => {
  const list = (labels || []).filter(Boolean);
  if (!list.length) {
    return "Seat labels must be unique in each section.";
  }
  return `Seat labels must be unique in each section. Duplicate found: ${list.join(", ")}`;
};

const childrenOf = (sections, parentId) =>
  (sections || [])
    .filter((section) => (section.parent_section_id || null) === parentId)
    .slice()
    .sort((left, right) => (left.sort_order || 0) - (right.sort_order || 0));

export const getLeafSections = (sections = []) => {
  const topLevel = childrenOf(sections, null);
  const leaves = [];

  topLevel.forEach((parent) => {
    const kids = childrenOf(sections, parent.id);
    if (kids.length) {
      kids.forEach((child) => {
        const optionValue = `${parent.name} / ${child.name}`;
        leaves.push({
          ...child,
          parentName: parent.name,
          optionLabel: optionValue,
          optionValue,
        });
      });
      return;
    }

    leaves.push({
      ...parent,
      parentName: null,
      optionLabel: parent.name,
      optionValue: parent.name,
    });
  });

  return leaves;
};

export const countTopLevelSections = (sections = []) =>
  (sections || []).filter((section) => !section.parent_section_id).length;

export const getTopLevelSections = (sections = []) =>
  childrenOf(sections, null).map((section) => ({
    id: section.id,
    value: section.name,
    label: section.name,
    color: String(section.color || "").trim(),
  }));

export const getSeatAreaOptions = (sections = [], parentName) => {
  const parent = (sections || []).find(
    (section) => !section.parent_section_id && section.name === parentName
  );
  if (!parent) return [];

  const kids = childrenOf(sections, parent.id);
  if (kids.length) {
    return kids.map((child) => ({
      value: child.name,
      label: child.name,
      sectionId: child.id,
    }));
  }

  return [{ value: parent.name, label: parent.name, sectionId: parent.id }];
};

export const countSeatsForAreas = (sections = [], seats = [], parentName, areaLabels = []) => {
  const options = getSeatAreaOptions(sections, parentName);
  if (!options.length) return 0;

  const labels = areaLabels?.length ? areaLabels : options.map((option) => option.value);
  return options
    .filter((option) => labels.includes(option.value))
    .reduce((total, option) => total + countSeatsForSection(seats, option.sectionId), 0);
};

export const buildSeatAreaOverlapKey = (parentSection, areaLabel) =>
  `${parentSection}::${areaLabel}`;

export const findSeatAreaOverlap = (tiers = []) => {
  const claimed = new Map();

  for (const tier of tiers || []) {
    const parent = String(tier.section || "").trim();
    const areas = tier.seatAreas || tier.seat_areas || [];
    if (!parent || !areas.length) continue;

    for (const areaLabel of areas) {
      const key = buildSeatAreaOverlapKey(parent, areaLabel);
      if (claimed.has(key)) {
        return { areaLabel, otherTierName: claimed.get(key) };
      }
      claimed.set(key, tier.name || parent);
    }
  }

  return null;
};

export const buildPreviewPlanForParent = ({
  sections = [],
  seats = [],
  parentName,
  seatAreas = [],
} = {}) => {
  const parent = (sections || []).find(
    (section) => !section.parent_section_id && section.name === parentName
  );
  if (!parent) return null;

  const kids = childrenOf(sections, parent.id);
  const leaves = kids.length ? kids : [parent];
  let pages = leaves.map((leaf) =>
    toPage(
      leaf,
      (seats || []).filter((seat) => seat.section_id === leaf.id),
      parent.color
    )
  );

  if (seatAreas.length) {
    pages = pages.filter((page) => seatAreas.includes(page.label));
  }

  if (!pages.length) return null;

  return {
    label: parent.name,
    description: "Physical seat layout",
    pages,
    canonical: true,
  };
};

const toPage = (section, seats = [], color = "") => {
  const { startRowIndex, startColumnNumber, numberDirection } = sectionOrigin(section);
  const layout = seatsToLayout(section.row_count || 0, section.column_count || 0, seats);

  return {
    id: section.id,
    label: section.name,
    color: String(color || section.color || "").trim(),
    rows: rowLabelsForCount(section.row_count || 0, startRowIndex),
    columns: section.column_count || 0,
    start_row_label: rowIndexToLabel(startRowIndex),
    start_column_number: startColumnNumber,
    number_direction: numberDirection,
    layout,
    seat_numbers: seatNumbersForLayout(layout, startColumnNumber, numberDirection),
  };
};

export const buildSeatLayoutSnapshot = ({ venue, sections = [], seats = [], sectionColors = {} } = {}) => {
  const seatsBySection = {};
  seats.forEach((seat) => {
    if (!seat?.section_id) return;
    if (!seatsBySection[seat.section_id]) seatsBySection[seat.section_id] = [];
    seatsBySection[seat.section_id].push(seat);
  });

  const topLevel = childrenOf(sections, null);

  return {
    version: 1,
    venue_id: venue?.id || null,
    venue_name: venue?.name || "",
    captured_at: new Date().toISOString(),
    sections: topLevel.map((parent) => {
      const kids = childrenOf(sections, parent.id);
      const parentColor = String(sectionColors[parent.name] || parent.color || "").trim();
      const pages = (kids.length ? kids : [parent]).map((leaf) =>
        toPage(leaf, seatsBySection[leaf.id] || [], parentColor)
      );
      return {
        id: parent.id,
        name: parent.name,
        color: parentColor,
        pages,
      };
    }),
  };
};

export const countSeatsForSection = (seats, sectionId) =>
  (seats || []).filter((seat) => seat.section_id === sectionId).length;

export const sectionToPreviewPlan = (section, seats = []) => {
  if (!section) return null;
  const sectionSeats = (seats || []).filter(
    (seat) => !seat.section_id || seat.section_id === section.id
  );
  return {
    label: section.optionLabel || section.name,
    description: "Physical seat layout",
    pages: [toPage(section, sectionSeats)],
  };
};
